// ✅ Trekkin.app — useAuthStore.jsx v6.7.1 (SUBSCRIPTION NO-WRITE + OFFLINE SESSION STICKY + iOS PERSISTENCE)
// ------------------------------------------------------------------
// 🔹 Base = v6.7.0 (mantém TUDO)
// 🔹 FIX: cliente NÃO escreve em /userSubscriptions (rules = write:false)
// 🔹 O plano vem apenas do webhook (Admin SDK) + listener (read-only)
// 🔹 Remove warnings permission_denied no boot/login sem mexer na UX
// 🔹 Regra de Ouro: nada removido, só estabilizado
// ------------------------------------------------------------------

import { create } from "zustand";
import { useEventsStore } from "./useEventsStore";
import { useGroupStore } from "./useGroupStore";
import { useLogsStore } from "./useLogsStore";

import {
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  OAuthProvider,
  updateProfile,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  sendPasswordResetEmail,
  getAuth,
  deleteUser,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  signInWithCredential,
} from "firebase/auth";

import {
  ref,
  get,
  set,
  update,
  remove,
  serverTimestamp,
  onDisconnect,
  onValue,
  query,
  orderByChild,
  equalTo,
} from "firebase/database";

import { auth, db } from "../firebaseConfig";
import { usePanelsStore } from "./usePanelsStore";
import { showToast } from "../components/NotifyToast.jsx";
import {
  getPendingUserInviteToken,
  clearPendingUserInviteToken,
  fetchReferralApi,
} from "../utils/referrals.js";
import { awardDailyLoginXp } from "../utils/gamificationXp.js";

function detectCurrentUserPlatform() {
  if (typeof window === "undefined") {
    return { platform: "web", runtime: "web" };
  }
  const userAgent = String(window.navigator?.userAgent || "");
  const hasIosBridge =
    typeof window.webkit?.messageHandlers?.nativeBridge?.postMessage === "function";
  if (hasIosBridge && /iPad|iPhone|iPod/i.test(userAgent)) {
    return { platform: "ios", runtime: "ios-wkwebview" };
  }
  const capacitor = window.Capacitor;
  const capacitorPlatform = String(capacitor?.getPlatform?.() || "").toLowerCase();
  if (capacitorPlatform === "android") {
    return { platform: "android", runtime: "capacitor" };
  }
  if (capacitorPlatform === "ios") {
    return { platform: "ios", runtime: "capacitor" };
  }
  return { platform: "web", runtime: "web" };
}

// -----------------------------------------------------------
// Helpers
// -----------------------------------------------------------
function formatDateDDMMAAAA(ts) {
  try {
    const d = new Date(ts);
    return `${String(d.getDate()).padStart(2, "0")}${String(
      d.getMonth() + 1
    ).padStart(2, "0")}${d.getFullYear()}`;
  } catch {
    return "";
  }
}

function isValidUsername(username) {
  if (!username) return false;
  return /^[a-z0-9_]{3,20}$/.test(username.trim());
}

function isOnlineSafe() {
  try {
    return typeof navigator !== "undefined" ? navigator.onLine !== false : true;
  } catch {
    return true;
  }
}

function getNativeFirebaseAuthPlugin() {
  try {
    if (!isNativeAppSafe()) return null;
    return (
      globalThis?.Capacitor?.Plugins?.FirebaseAuthentication ||
      globalThis?.Capacitor?.Plugins?.CapacitorFirebaseAuthentication ||
      null
    );
  } catch {
    return null;
  }
}

function extractNativeCredential(result) {
  const cred = result?.credential || {};
  return {
    idToken: cred?.idToken || result?.idToken || null,
    accessToken: cred?.accessToken || result?.accessToken || null,
    rawNonce: cred?.nonce || result?.rawNonce || result?.nonce || null,
  };
}

function hasWebViewNativeBridge() {
  try {
    return !!globalThis?.webkit?.messageHandlers?.nativeBridge;
  } catch {
    return false;
  }
}

function isNativeAppSafe() {
  try {
    return (
      !!globalThis?.Capacitor?.isNativePlatform?.() ||
      hasWebViewNativeBridge()
    );
  } catch {
    return false;
  }
}

function callWebViewNativeAuth(action, timeoutMs = 20000) {
  return new Promise((resolve, reject) => {
    if (!hasWebViewNativeBridge()) {
      reject(new Error("native-bridge-unavailable"));
      return;
    }

    const eventName =
      action === "loginGoogleNative" ? "native:googleAuth" : "native:appleAuth";
    try {
      useLogsStore
        .getState()
        .addLog("system", `[AUTH] native bridge -> ${action}`, {
          eventName,
        });
    } catch {}

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error("native-bridge-timeout"));
    }, timeoutMs);

    const onEvent = (ev) => {
      const detail = ev?.detail || {};
      try {
        useLogsStore
          .getState()
          .addLog("system", `[AUTH] native bridge <- ${action}`, detail);
      } catch {}
      cleanup();
      if (detail?.error) {
        reject(new Error(detail.error));
        return;
      }
      resolve(detail);
    };

    function cleanup() {
      clearTimeout(timer);
      globalThis.removeEventListener(eventName, onEvent);
    }

    globalThis.addEventListener(eventName, onEvent, { once: true });
    globalThis.webkit.messageHandlers.nativeBridge.postMessage({ action });
  });
}

const BLOCKED_LOGIN_MESSAGE = "Erro de login. Contactar o suporte.";

function isAppAccessBlocked(profile) {
  return profile?.appBlocked === true;
}

async function getActivePlanForUid(uid) {
  if (!uid) return { plan: null, active: false, data: null };
  try {
    const snap = await get(ref(db, `userSubscriptions/${uid}`));
    if (!snap.exists()) return { plan: null, active: false, data: null };
    const data = snap.val() || {};
    const planRaw = data.activePlan || data.plan || null;
    const plan = planRaw ? String(planRaw).toLowerCase() : null;
    const status = String(data.status || "active").toLowerCase();
    const expiresAt = Number(data.expiresAt || 0);
    const now = Date.now();
    const active =
      !!plan &&
      plan !== "free" &&
      status === "active" &&
      (!expiresAt || now < expiresAt);
    return { plan, active, data };
  } catch {
    return { plan: null, active: false, data: null };
  }
}

function resolveProfileRole(profile, planInfo) {
  const storedRole = String(profile?.role || "free").toLowerCase();
  if (storedRole === "admin") return "admin";

  const activePlan = planInfo?.active
    ? String(planInfo.plan || "").toLowerCase()
    : "";
  if (activePlan && activePlan !== "free") return activePlan;

  const subscribedPlan = String(planInfo?.plan || "").toLowerCase();
  if (subscribedPlan && subscribedPlan !== "free" && storedRole === subscribedPlan) {
    return "free";
  }

  return storedRole || "free";
}

function markAuthReady() {
  if (typeof window !== "undefined") {
    window.__TREKKIN_AUTH_READY__ = true;
  }
}

function startEventsListenerSafe(label = "sessao") {
  try {
    const eventsStore = useEventsStore.getState();
    if (typeof eventsStore.listenEvents === "function") {
      eventsStore.listenEvents();
    }
  } catch (e) {
    console.warn("[AUTH] falha ao iniciar listenEvents", e);
  }
}

function toFriendlyAuthError(err, fallbackMsg) {
  const code = String(err?.code || "").toLowerCase();
  const msg = String(err?.message || "");
  if (
    code.includes("auth/invalid-credential") ||
    code.includes("auth/wrong-password") ||
    code.includes("auth/invalid-login-credentials")
  ) {
    return "Email ou palavra-passe inválidos.";
  }
  if (code.includes("auth/user-not-found")) {
    return "Conta não encontrada para este email.";
  }
  if (code.includes("auth/invalid-email")) {
    return "Email inválido.";
  }
  if (code.includes("auth/too-many-requests")) {
    return "Muitas tentativas de login. Tenta novamente em alguns minutos.";
  }
  if (
    code.includes("auth/disallowed-useragent") ||
    msg.toLowerCase().includes("disallowed_useragent")
  ) {
    return "Login Google bloqueado neste ambiente. Atualiza a app para usar login nativo Google.";
  }
  if (code.includes("auth/account-exists-with-different-credential")) {
    return "Esta conta já existe com outro método. Entra com o método existente e depois associa o login social no perfil.";
  }
  if (code.includes("auth/operation-not-allowed")) {
    return "Login Apple indisponível: ativa o provider Apple no Firebase Auth.";
  }
  if (code.includes("auth/unauthorized-domain")) {
    let host = "";
    let origin = "";
    try {
      host = String(globalThis?.location?.hostname || "");
      origin = String(globalThis?.location?.origin || "");
    } catch {}
    if (host || origin) {
      return `Login social indisponível: domínio não autorizado no Firebase Auth (host='${host || "-"}', origin='${origin || "-"}').`;
    }
    return "Login social indisponível: domínio não autorizado no Firebase Auth.";
  }
  if (code.includes("auth/popup-closed-by-user")) {
    return "Login social cancelado pelo utilizador.";
  }
  if (code.includes("auth/cancelled-popup-request")) {
    return "Login social cancelado.";
  }
  return err?.message || fallbackMsg;
}

async function finalizeSocialAuth(set, fbUser) {
  if (!fbUser?.uid) {
    set({ loading: false, error: "Não foi possível concluir a autenticação." });
    return { error: "Não foi possível concluir a autenticação." };
  }

  const uid = fbUser.uid;
  const emailRaw = String(fbUser.email || "").trim().toLowerCase();
  const safeName =
    String(fbUser.displayName || "").trim() ||
    (emailRaw ? emailRaw.split("@")[0] : "Trekker");
  const photoURL = fbUser.photoURL || null;
  const phoneNumber = fbUser.phoneNumber || null;

  const [profile, planInfo] = await Promise.all([
    get(ref(db, `users/${uid}`))
      .then((snap) => (snap.exists() ? snap.val() : {}))
      .catch(() => ({})),
    getActivePlanForUid(uid).catch(() => null),
  ]);

  if (isAppAccessBlocked(profile)) {
    try {
      await signOut(auth);
    } catch {}
    localStorage.removeItem("trekkin_session");
    set({
      loading: false,
      user: null,
      permissions: {},
      activePlan: null,
      permissionsLoaded: false,
      error: BLOCKED_LOGIN_MESSAGE,
      offline: false,
      sessionRestored: false,
      authReady: true,
    });
    try {
      usePanelsStore.getState().openPanel("login");
    } catch {}
    return { error: BLOCKED_LOGIN_MESSAGE };
  }

  const planRole = planInfo?.active ? planInfo.plan : null;
  const resolvedRole = resolveProfileRole(profile, planInfo);

  const userData = {
    uid,
    email: emailRaw || profile.email || null,
    name: profile.name || safeName,
    role: resolvedRole || "free",
    firstName: profile.firstName || null,
    lastName: profile.lastName || null,
    username: profile.username || null,
    dob: profile.dob || null,
    weightKg: Number(String(profile.weightKg ?? "").replace(",", ".")) || null,
    weightUpdatedAt: Number(profile.weightUpdatedAt) || 0,
    weightSource: profile.weightSource || null,
    ccNumber: profile.ccNumber || null,
    emergencyContact: profile.emergencyContact || null,
    photoURL: profile.photoURL || photoURL,
    phoneNumber: profile.phoneNumber || phoneNumber,
    sex: profile.sex || profile.gender || null,
    gender: profile.gender || profile.sex || null,
    guideDescription: profile.guideDescription || null,
  };

  Promise.resolve().then(() => ensureUserRecord(userData));
  Promise.resolve().then(() => ensureUserSubscription(uid));

  set({
    user: userData,
    activePlan: planRole || null,
    loading: false,
    offline: false,
    error: null,
  });
  attachRolePermissionListener(userData.role);
  attachUserListener(userData.uid);
  attachUserPlanListener(userData.uid);
  persistSessionSafe();
  markAuthReady();
  startEventsListenerSafe("login-social");

  return true;
}

// -----------------------------------------------------------
// ensureUserRecord
// -----------------------------------------------------------
async function ensureUserRecord({
  uid,
  email,
  name,
  role,
  firstName,
  lastName,
  username,
  dob,
  weightKg,
  ccNumber,
  emergencyContact,
  photoURL,
  phoneNumber,
  sex,
  gender,
  guideDescription,
}) {
  if (!uid) return;

  const normalizedEmail = (email || "").trim().toLowerCase();
  const usernameKey = (username || "").trim().toLowerCase() || null;

  const now = Date.now();
  const lastSeenText = formatDateDDMMAAAA(now);
  const userRef = ref(db, `users/${uid}`);

  try {
    const snap = await get(userRef);
    let finalUser = {};

    if (!snap.exists()) {
      const payload = {
        uid,
        email: normalizedEmail || null,
        name:
          name || (normalizedEmail ? normalizedEmail.split("@")[0] : "Trekker"),
        role: role || "free",
        createdAt: serverTimestamp(),
        online: true,
        lastSeen: serverTimestamp(),
        lastSeenText,
      };

      if (firstName) payload.firstName = firstName;
      if (lastName) payload.lastName = lastName;
      if (usernameKey) payload.username = usernameKey;
      if (dob) payload.dob = dob;
      if (weightKg) payload.weightKg = Number(String(weightKg).replace(",", "."));
      if (ccNumber) payload.ccNumber = ccNumber;
      if (emergencyContact) payload.emergencyContact = emergencyContact;
      if (photoURL) payload.photoURL = photoURL;
      if (phoneNumber) payload.phoneNumber = phoneNumber;
      if (sex) payload.sex = sex;
      if (gender || sex) payload.gender = gender || sex;
      if (guideDescription) payload.guideDescription = guideDescription;

      await set(userRef, payload);
      finalUser = payload;
    } else {
      const prev = snap.val() || {};
      const payload = {
        uid,
        email: normalizedEmail || null, // 🔒 nunca confiar no prev.email
        name:
          name ||
          prev.name ||
          (normalizedEmail ? normalizedEmail.split("@")[0] : "Trekker"),
        role: role || prev.role || "free",
        online: true,
        lastSeen: serverTimestamp(),
        lastSeenText,
      };

      // Backfill: registos antigos podem não ter createdAt.
      if (
        prev?.createdAt == null &&
        prev?.registeredAt == null &&
        prev?.signupAt == null &&
        prev?.joinedAt == null &&
        prev?.created_at == null
      ) {
        payload.createdAt = serverTimestamp();
      }

      if (firstName || prev.firstName)
        payload.firstName = firstName || prev.firstName;
      if (lastName || prev.lastName)
        payload.lastName = lastName || prev.lastName;
      if (usernameKey || prev.username)
        payload.username = usernameKey || prev.username;
      if (dob || prev.dob) payload.dob = dob || prev.dob;
      if (weightKg || prev.weightKg)
        payload.weightKg = Number(String(weightKg || prev.weightKg).replace(",", "."));
      if (ccNumber || prev.ccNumber)
        payload.ccNumber = ccNumber || prev.ccNumber;
      if (emergencyContact || prev.emergencyContact)
        payload.emergencyContact = emergencyContact || prev.emergencyContact;
      if (photoURL || prev.photoURL)
        payload.photoURL = photoURL || prev.photoURL;
      if (phoneNumber || prev.phoneNumber)
        payload.phoneNumber = phoneNumber || prev.phoneNumber;
      if (sex || prev.sex) payload.sex = sex || prev.sex;
      if (gender || sex || prev.gender || prev.sex)
        payload.gender = gender || sex || prev.gender || prev.sex;
      if (guideDescription || prev.guideDescription)
        payload.guideDescription = guideDescription || prev.guideDescription;

      await update(userRef, payload);
      finalUser = { ...prev, ...payload };
    }

    // -----------------------------------------------------------
    // 🔓 usersPublic (espelho público controlado)
    // -----------------------------------------------------------
    const publicRef = ref(db, `usersPublic/${uid}`);

    const publicPayload = {
      uid,
      name:
        finalUser.name ||
        `${firstName || ""} ${lastName || ""}`.trim() ||
        "Trekker",
      username: finalUser.username || null,
      role: finalUser.role || "free",
      photoURL: finalUser.photoURL || null,
      guideDescription: finalUser.guideDescription || null,
      sex: finalUser.sex || finalUser.gender || null,
      gender: finalUser.gender || finalUser.sex || null,
      updatedAt: serverTimestamp(),
    };

    await update(publicRef, publicPayload);

    if (usernameKey && !snap.exists()) {
      await set(ref(db, `usernames/${usernameKey}`), uid);
    }

    startUserPresence(uid);
  } catch (e) {
    console.warn("ensureUserRecord falhou:", e);
  }
}

// -----------------------------------------------------------
// Permissions Listener
// -----------------------------------------------------------
let currentPermissionUnsub = null;

function persistSessionSafe(extra = {}) {
  try {
    const st = useAuthStore.getState();
    const u = st.user;
    if (!u?.uid) return;

    localStorage.setItem(
      "trekkin_session",
      JSON.stringify({
        user: { ...u },
        permissions: st.permissions || {},
        ts: Date.now(),
        ...extra,
      })
    );
  } catch {}
}

function attachRolePermissionListener(role) {
  if (!role) return;

  const wantRole = String(role).toLowerCase();

  // 🔑 ADMIN = bypass total (não depende de /permissions)
  if (wantRole === "admin") {
    if (currentPermissionUnsub) {
      currentPermissionUnsub();
      currentPermissionUnsub = null;
    }

    useAuthStore.setState({
      permissions: { __admin: true },
      permissionsLoaded: true,
    });

    console.log("🔐 [PERMISSIONS] admin → bypass total");
    persistSessionSafe();
    return;
  }

  // 🛟 FALLBACK REAL — role inválido (apenas isto!)
  if (!wantRole) {
    if (currentPermissionUnsub) {
      currentPermissionUnsub();
      currentPermissionUnsub = null;
    }

    useAuthStore.setState({
      permissions: {},
      permissionsLoaded: true,
    });

    console.log("🔐 [PERMISSIONS] fallback → role inválido");
    persistSessionSafe();
    return;
  }

  if (currentPermissionUnsub) {
    currentPermissionUnsub();
    currentPermissionUnsub = null;
  }

  const roleRef = ref(db, `permissions/${wantRole}`);
  console.log(`🔐 [PERMISSIONS] listening → permissions/${wantRole}`);

  currentPermissionUnsub = onValue(
    roleRef,
    (snap) => {
      const perms = snap.val() || {};
      useAuthStore.setState({
        permissions: perms,
        permissionsLoaded: true,
      });
      persistSessionSafe();
    },
    (err) => {
      console.warn(`⚠️ [PERMISSIONS] denied permissions/${wantRole}`, err);
      // ✅ CRÍTICO: não bloquear UI (uploads incluídos)
      useAuthStore.setState({ permissionsLoaded: true });
    }
  );
}

// -----------------------------------------------------------
// ensureUserSubscription (READ-ONLY / NO-OP)
// -----------------------------------------------------------
// ⚠️ IMPORTANTE:
// - Rules: /userSubscriptions/$uid -> write:false
// - Quem escreve é o webhook (Admin SDK) no server.
// - O client só LÊ (attachUserPlanListener).
async function ensureUserSubscription(uid) {
  if (!uid) return;

  try {
    const subRef = ref(db, `userSubscriptions/${uid}`);
    const snap = await get(subRef);

    // Se não existe, NÃO criamos (evita permission_denied)
    if (!snap.exists()) {
      return;
    }
  } catch (e) {
    // offline / permissão / etc → falha silenciosa (mas sem writes!)
    console.warn("ensureUserSubscription (no-write) falhou:", e);
  }
}

// -----------------------------------------------------------
// 🔔 Subscription / Plan Listener
// -----------------------------------------------------------
let currentPlanUnsub = null;
let currentUserUnsub = null;
let currentPlanExpiryTimer = null;
let currentSubscriptionPlan = null;
let currentSubscriptionActive = false;
let currentPresenceUid = null;
let currentPresenceUnsub = null;
let currentPresenceHeartbeat = null;

const PRESENCE_HEARTBEAT_MS = 60 * 1000;

function clearPresenceHeartbeat() {
  if (currentPresenceHeartbeat) {
    clearInterval(currentPresenceHeartbeat);
    currentPresenceHeartbeat = null;
  }
}

function stopUserPresence() {
  clearPresenceHeartbeat();
  if (currentPresenceUnsub) {
    currentPresenceUnsub();
    currentPresenceUnsub = null;
  }
  currentPresenceUid = null;
}

function startUserPresence(uid) {
  if (!uid) return;
  if (currentPresenceUid === uid && currentPresenceUnsub) return;

  stopUserPresence();
  currentPresenceUid = uid;

  currentPresenceUnsub = onValue(
    ref(db, ".info/connected"),
    async (snap) => {
      if (currentPresenceUid !== uid) return;

      clearPresenceHeartbeat();
      if (snap.val() !== true) return;

      const onlineRef = ref(db, `users/${uid}/online`);
      const lastSeenRef = ref(db, `users/${uid}/lastSeen`);
      const lastSeenTextRef = ref(db, `users/${uid}/lastSeenText`);
      const currentPlatform = detectCurrentUserPlatform();

      try {
        await Promise.all([
          onDisconnect(onlineRef).set(false),
          onDisconnect(lastSeenRef).set(serverTimestamp()),
          onDisconnect(lastSeenTextRef).set(
            formatDateDDMMAAAA(Date.now())
          ),
        ]);

        await update(ref(db, `users/${uid}`), {
          online: true,
          lastSeen: serverTimestamp(),
          lastSeenText: formatDateDDMMAAAA(Date.now()),
          lastPlatform: currentPlatform.platform,
          lastRuntime: currentPlatform.runtime,
          lastPlatformSeenAt: serverTimestamp(),
          [`platforms/${currentPlatform.platform}/lastSeen`]: serverTimestamp(),
          [`platforms/${currentPlatform.platform}/runtime`]: currentPlatform.runtime,
        });

        currentPresenceHeartbeat = setInterval(() => {
          if (currentPresenceUid !== uid) return;
          update(ref(db, `users/${uid}`), {
            online: true,
            lastSeen: serverTimestamp(),
            lastSeenText: formatDateDDMMAAAA(Date.now()),
            lastPlatform: currentPlatform.platform,
            lastRuntime: currentPlatform.runtime,
            lastPlatformSeenAt: serverTimestamp(),
            [`platforms/${currentPlatform.platform}/lastSeen`]: serverTimestamp(),
            [`platforms/${currentPlatform.platform}/runtime`]: currentPlatform.runtime,
          }).catch(() => {});
        }, PRESENCE_HEARTBEAT_MS);
      } catch (e) {
        console.warn("Presença RTDB indisponível:", e);
      }
    },
    (e) => {
      console.warn("Ligação de presença RTDB indisponível:", e);
    }
  );
}

function clearUserPlanListener() {
  if (currentPlanUnsub) {
    currentPlanUnsub();
    currentPlanUnsub = null;
  }
  if (currentPlanExpiryTimer) {
    clearTimeout(currentPlanExpiryTimer);
    currentPlanExpiryTimer = null;
  }
  currentSubscriptionPlan = null;
  currentSubscriptionActive = false;
}

function attachUserPlanListener(uid) {
  if (!uid) return;

  clearUserPlanListener();

  const subRef = ref(db, `userSubscriptions/${uid}`);

  currentPlanUnsub = onValue(
    subRef,
    (snap) => {
      const data = snap.val();
      if (currentPlanExpiryTimer) {
        clearTimeout(currentPlanExpiryTimer);
        currentPlanExpiryTimer = null;
      }

      const st = useAuthStore.getState();
      const user = st.user;
      if (!user) return;

      const now = Date.now();
      const overrideStartsAt = Number(user.roleOverrideStartsAt || 0) || 0;
      const overrideUntil = Number(user.roleOverrideUntil || 0) || 0;
      const overrideActive =
        user.roleOverride && (!overrideStartsAt || overrideStartsAt <= now) && overrideUntil > now;

      const planRaw = data?.activePlan || data?.plan || null;
      const plan = planRaw ? String(planRaw).toLowerCase() : null;
      const status = String(data?.status || "active").toLowerCase();
      const expiresAt = Number(data?.expiresAt || 0);
      const active =
        !!plan &&
        plan !== "free" &&
        status === "active" &&
        (!expiresAt || now < expiresAt);
      const activePlan = active ? plan : null;
      currentSubscriptionPlan = plan;
      currentSubscriptionActive = active;
      useAuthStore.setState({ activePlan });

      // 🧠 SEM SUBSCRIÇÃO → ROLE MANDA
      if (!plan) {
        console.log("ℹ️ [PLAN] sem subscrição → mantém role:", user.role);
        return;
      }

      // 🔒 ADMIN É IMUTÁVEL (mesmo antes do plan listener)
      if (
        user.role === "admin" ||
        st.permissions?.__admin === true
      ) {
        console.log("🔒 [PLAN] admin ignora subscrição");
        return;
      }

      if (overrideActive) {
        // Mantém override enquanto estiver ativo
        return;
      }

      if (!active) {
        const currentRole = String(user.role || "free").toLowerCase();
        const roleBase = String(user.roleBase || "").toLowerCase();
        if (currentRole === plan || roleBase === plan) {
          const updatedUser = {
            ...user,
            role: "free",
            roleBase: "free",
          };
          useAuthStore.setState({ user: updatedUser });
          attachRolePermissionListener("free");
          persistSessionSafe({ plan: null });
        }
        return;
      }

      // ⬆️ só upgrade real
      if (user.role !== activePlan) {
        console.log("⬆️ [PLAN] upgrade:", user.role, "→", plan);

        const updatedUser = {
          ...user,
          role: activePlan,
        };

        useAuthStore.setState({ user: updatedUser });
        attachRolePermissionListener(activePlan);
        persistSessionSafe({ plan: activePlan });
      }

      if (expiresAt > now) {
        currentPlanExpiryTimer = setTimeout(() => {
          currentPlanExpiryTimer = null;
          attachUserPlanListener(uid);
        }, Math.min(expiresAt - now + 250, 2147483647));
      }
    },
    (err) => {
      console.warn("⚠️ [PLAN] erro a ouvir subscrição:", err);
    }
  );
}

// -----------------------------------------------------------
// 🔔 User Listener (remote flags + live user changes)
// -----------------------------------------------------------
function attachUserListener(uid) {
  if (!uid) return;

  if (currentUserUnsub) {
    currentUserUnsub();
    currentUserUnsub = null;
  }

  const userRef = ref(db, `users/${uid}`);
  currentUserUnsub = onValue(
    userRef,
    (snap) => {
      const data = snap.val() || {};
      const st = useAuthStore.getState();
      const prev = st.user;
      if (!prev || prev.uid !== uid) return;

      if (isAppAccessBlocked(data)) {
        if (currentPermissionUnsub) {
          currentPermissionUnsub();
          currentPermissionUnsub = null;
        }
        clearUserPlanListener();
        if (currentUserUnsub) {
          currentUserUnsub();
          currentUserUnsub = null;
        }
        try {
          const eventsStore = useEventsStore.getState();
          if (typeof eventsStore.stopListeningEvents === "function") {
            eventsStore.stopListeningEvents();
          }
        } catch {}
        try {
          signOut(auth);
        } catch {}
        localStorage.removeItem("trekkin_session");
        useAuthStore.setState({
          user: null,
          permissions: {},
          activePlan: null,
          permissionsLoaded: false,
          loading: false,
          error: BLOCKED_LOGIN_MESSAGE,
          offline: false,
          sessionRestored: false,
          authReady: true,
        });
        try {
          usePanelsStore.getState().openPanel("login");
        } catch {}
        return;
      }

      const planRole = String(st.activePlan || "").toLowerCase();
      let nextRole = (data.role || prev.role || "free").toLowerCase();
      if (
        !currentSubscriptionActive &&
        currentSubscriptionPlan &&
        nextRole === currentSubscriptionPlan
      ) {
        nextRole = "free";
      }
      if (planRole && planRole !== "free" && nextRole === "free") {
        nextRole = planRole;
      }
      const now = Date.now();
      const overrideStartsAt = Number(data.roleOverrideStartsAt || 0) || 0;
      const overrideUntil = Number(data.roleOverrideUntil || 0) || 0;
      const overrideActive =
        data.roleOverride && (!overrideStartsAt || overrideStartsAt <= now) && overrideUntil > now;
      const effectiveRole =
        !overrideActive || nextRole === "admin"
          ? nextRole
          : String(data.roleOverride || nextRole).toLowerCase();

      const next = {
        ...prev,
        ...data,
        uid: prev.uid,
        email: prev.email || data.email || null,
        name: data.name || prev.name,
        role: effectiveRole,
        roleBase: nextRole,
      };

      useAuthStore.setState({ user: next });

      if (effectiveRole && effectiveRole !== prev.role) {
        attachRolePermissionListener(effectiveRole);
      }

      try {
        const setLogsEnabled = useLogsStore.getState?.().setLogsEnabled;
        if (typeof setLogsEnabled === "function") {
          if (data.logRemoteEnabled === true) setLogsEnabled(true);
          if (data.logRemoteEnabled === false) setLogsEnabled(false);
        }
      } catch {}
    },
    (err) => {
      console.warn("⚠️ [USER] erro a ouvir users/{uid}:", err);
    }
  );
}

// -----------------------------------------------------------
// STORE
// -----------------------------------------------------------
export const useAuthStore = create((set) => ({
  user: null,
  users: [],
  permissions: {},
  activePlan: null,
  permissionsLoaded: false,
  loading: false,
  error: null,
  offline: false,
  sessionRestored: false,
  authReady: false,

  // 🆕 helper explícito
  setOffline: (val) => {
    set({ offline: !!val });
  },

  // -------------------------------------------------------------
  // LOGIN
  // -------------------------------------------------------------
  login: async (email, password) => {
    const normalizedEmail = (email || "").trim().toLowerCase();
    set({ loading: true, error: null });

    // 🧠 Sem net: não dá para validar credenciais no Firebase.
    // Mas se já existir sessão, restauramos e seguimos.
    if (!isOnlineSafe()) {
      try {
        const raw = localStorage.getItem("trekkin_session");
        if (raw) {
          const data = JSON.parse(raw);
          if (data?.user?.uid) {
            set({
              user: data.user,
              permissions: data.permissions || {},
              permissionsLoaded: true,
              loading: false,
              error: null,
              offline: true,
              sessionRestored: true,
              authReady: true,
            });
            // tenta re-ligar listener (vai falhar offline, mas ok)
            if (data.user.role) attachRolePermissionListener(data.user.role);
            if (data.user.uid) attachUserPlanListener(data.user.uid);
            return true;
          }
        }
      } catch {}
      set({
        loading: false,
        offline: true,
        error: "Sem internet. Liga-te para fazer login.",
      });
      return { error: "Sem internet. Liga-te para fazer login." };
    }

    try {
      const userCred = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );

      const uid = userCred.user.uid;
      const [profile, planInfo] = await Promise.all([
        get(ref(db, `users/${uid}`))
          .then((snap) => (snap.exists() ? snap.val() : {}))
          .catch(() => ({})),
        getActivePlanForUid(uid).catch(() => null),
      ]);

      if (isAppAccessBlocked(profile)) {
        try {
          await signOut(auth);
        } catch {}
        localStorage.removeItem("trekkin_session");
        set({
          loading: false,
          user: null,
          permissions: {},
          activePlan: null,
          permissionsLoaded: false,
          error: BLOCKED_LOGIN_MESSAGE,
          offline: false,
          sessionRestored: false,
          authReady: true,
        });
        try {
          usePanelsStore.getState().openPanel("login");
        } catch {}
        return { error: BLOCKED_LOGIN_MESSAGE };
      }

      const planRole = planInfo?.active ? planInfo.plan : null;
      const resolvedRole = resolveProfileRole(profile, planInfo);

      const userData = {
        uid,
        email: normalizedEmail,
        name: profile.name || normalizedEmail.split("@")[0],
        role: resolvedRole || "free",
        firstName: profile.firstName || null,
        lastName: profile.lastName || null,
        username: profile.username || null,
        dob: profile.dob || null,
        weightKg: Number(String(profile.weightKg ?? "").replace(",", ".")) || null,
        weightUpdatedAt: Number(profile.weightUpdatedAt) || 0,
        weightSource: profile.weightSource || null,
        ccNumber: profile.ccNumber || null,
        emergencyContact: profile.emergencyContact || null,
        photoURL: profile.photoURL || null,
        phoneNumber: profile.phoneNumber || null,
        sex: profile.sex || profile.gender || null,
        gender: profile.gender || profile.sex || null,
        guideDescription: profile.guideDescription || null,
      };

      Promise.resolve().then(() => ensureUserRecord(userData));
      Promise.resolve().then(() => ensureUserSubscription(uid));

      set({
        user: userData,
        activePlan: planRole || null,
        loading: false,
        offline: false,
      });
      attachRolePermissionListener(userData.role);
      attachUserListener(userData.uid);
      attachUserPlanListener(userData.uid);
      persistSessionSafe();
      markAuthReady();
      startEventsListenerSafe("login");

      return true;
    } catch (err) {
      const friendly = toFriendlyAuthError(err, "Erro no login.");
      console.warn("[AUTH][email-login]", {
        code: err?.code || null,
        message: err?.message || null,
      });
      set({ loading: false, error: friendly });
      return { error: friendly };
    }
  },

  loginWithGoogle: async () => {
    set({ loading: true, error: null });

    if (!isOnlineSafe()) {
      set({ loading: false, offline: true });
      return { error: "Sem internet. Liga-te para continuar." };
    }

    try {
      const nativePlugin = getNativeFirebaseAuthPlugin();
      if (nativePlugin?.signInWithGoogle) {
        try {
          useLogsStore.getState().addLog("system", "[AUTH] google native plugin");
        } catch {}
        const nativeResult = await nativePlugin.signInWithGoogle({
          skipNativeAuth: true,
        });
        const { idToken, accessToken } = extractNativeCredential(nativeResult);
        if (!idToken && !accessToken) {
          throw new Error("native-google-token-missing");
        }
        const firebaseCredential = GoogleAuthProvider.credential(
          idToken || null,
          accessToken || null
        );
        try {
          useLogsStore.getState().addLog("system", "[AUTH] google signInWithCredential");
        } catch {}
        const cred = await signInWithCredential(auth, firebaseCredential);
        return await finalizeSocialAuth(set, cred?.user || null);
      }

      if (isNativeAppSafe()) {
        if (hasWebViewNativeBridge()) {
          try {
            useLogsStore.getState().addLog("system", "[AUTH] google native bridge");
          } catch {}
          const nativeResult = await callWebViewNativeAuth("loginGoogleNative");
          const idToken = nativeResult?.idToken || null;
          const accessToken = nativeResult?.accessToken || null;
          if (!idToken && !accessToken) {
            throw new Error("native-google-token-missing");
          }
          const firebaseCredential = GoogleAuthProvider.credential(
            idToken || null,
            accessToken || null
          );
          try {
            useLogsStore.getState().addLog("system", "[AUTH] google signInWithCredential");
          } catch {}
          const cred = await signInWithCredential(auth, firebaseCredential);
          return await finalizeSocialAuth(set, cred?.user || null);
        }
        const msg =
          "Login Google indisponível nesta build nativa. Falta bridge nativa.";
        set({ loading: false, error: msg });
        return { error: msg };
      }

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });

      let credential = null;
      try {
        credential = await signInWithPopup(auth, provider);
      } catch (popupErr) {
        const code = String(popupErr?.code || "");
        const shouldRedirect =
          code.includes("popup-blocked") ||
          code.includes("popup-closed-by-user") ||
          code.includes("operation-not-supported-in-this-environment");

        if (!shouldRedirect) throw popupErr;
        await signInWithRedirect(auth, provider);
        set({ loading: false });
        return { pendingRedirect: true };
      }

      return await finalizeSocialAuth(set, credential?.user || null);
    } catch (err) {
      const msg = toFriendlyAuthError(err, "Falha no login com Google.");
      set({ loading: false, error: msg });
      return { error: msg };
    }
  },

  loginWithApple: async () => {
    set({ loading: true, error: null });

    if (!isOnlineSafe()) {
      set({ loading: false, offline: true });
      return { error: "Sem internet. Liga-te para continuar." };
    }

    try {
      const nativePlugin = getNativeFirebaseAuthPlugin();
      if (nativePlugin?.signInWithApple) {
        try {
          useLogsStore.getState().addLog("system", "[AUTH] apple native plugin");
        } catch {}
        const nativeResult = await nativePlugin.signInWithApple({
          skipNativeAuth: true,
        });
        const { idToken, rawNonce } = extractNativeCredential(nativeResult);
        if (!idToken) {
          throw new Error("native-apple-token-missing");
        }
        const provider = new OAuthProvider("apple.com");
        const firebaseCredential = provider.credential({
          idToken,
          rawNonce: rawNonce || undefined,
        });
        try {
          useLogsStore.getState().addLog("system", "[AUTH] apple signInWithCredential");
        } catch {}
        const cred = await signInWithCredential(auth, firebaseCredential);
        return await finalizeSocialAuth(set, cred?.user || null);
      }

      if (isNativeAppSafe()) {
        if (hasWebViewNativeBridge()) {
          try {
            useLogsStore.getState().addLog("system", "[AUTH] apple native bridge");
          } catch {}
          const nativeResult = await callWebViewNativeAuth("loginAppleNative");
          const idToken = nativeResult?.idToken || null;
          const rawNonce = nativeResult?.rawNonce || null;
          if (!idToken) {
            throw new Error("native-apple-token-missing");
          }
          const provider = new OAuthProvider("apple.com");
          const firebaseCredential = provider.credential({
            idToken,
            rawNonce: rawNonce || undefined,
          });
          try {
            useLogsStore.getState().addLog("system", "[AUTH] apple signInWithCredential");
          } catch {}
          const cred = await signInWithCredential(auth, firebaseCredential);
          return await finalizeSocialAuth(set, cred?.user || null);
        }
        const msg =
          "Login Apple indisponível nesta build nativa. Falta bridge nativa.";
        set({ loading: false, error: msg });
        return { error: msg };
      }

      const provider = new OAuthProvider("apple.com");
      provider.addScope("email");
      provider.addScope("name");

      let credential = null;
      try {
        credential = await signInWithPopup(auth, provider);
      } catch (popupErr) {
        const code = String(popupErr?.code || "");
        const shouldRedirect =
          code.includes("popup-blocked") ||
          code.includes("popup-closed-by-user") ||
          code.includes("operation-not-supported-in-this-environment");

        if (!shouldRedirect) throw popupErr;
        await signInWithRedirect(auth, provider);
        set({ loading: false });
        return { pendingRedirect: true };
      }

      return await finalizeSocialAuth(set, credential?.user || null);
    } catch (err) {
      const code = String(err?.code || "").toLowerCase();
      if (code.includes("auth/account-exists-with-different-credential")) {
        try {
          const email = String(err?.customData?.email || "").trim().toLowerCase();
          const pendingAppleCred = OAuthProvider.credentialFromError(err);
          if (email) {
            const methods = await fetchSignInMethodsForEmail(auth, email);
            if (methods.includes("google.com")) {
              const gProvider = new GoogleAuthProvider();
              gProvider.setCustomParameters({ prompt: "select_account" });
              let gCred = null;
              try {
                gCred = await signInWithPopup(auth, gProvider);
              } catch (gErr) {
                const gCode = String(gErr?.code || "");
                const gRedirect =
                  gCode.includes("popup-blocked") ||
                  gCode.includes("popup-closed-by-user") ||
                  gCode.includes("operation-not-supported-in-this-environment");
                if (gRedirect) {
                  await signInWithRedirect(auth, gProvider);
                  set({ loading: false });
                  return { pendingRedirect: true };
                }
                throw gErr;
              }

              if (pendingAppleCred && auth.currentUser) {
                try {
                  await linkWithCredential(auth.currentUser, pendingAppleCred);
                } catch {
                  // Se já estiver associado, continuamos normal.
                }
              }
              return await finalizeSocialAuth(set, gCred?.user || auth.currentUser);
            }

            if (methods.includes("password")) {
              const msg =
                "Esta conta já existe por email/password. Entra com email e palavra-passe para concluir.";
              set({ loading: false, error: msg });
              return { error: msg };
            }
          }
        } catch {
          // cai para mensagem friendly abaixo
        }
      }

      const msg = toFriendlyAuthError(err, "Falha no login com Apple.");
      set({ loading: false, error: msg });
      return { error: msg };
    }
  },

  // -------------------------------------------------------------
  // REGISTER (FIX FINAL)
  // -------------------------------------------------------------
  register: async (email, password, firstName, lastName, username) => {
    const normalizedEmail = (email || "").trim().toLowerCase();
    const uname = (username || "").trim().toLowerCase();

    set({ loading: true, error: null });

    if (!isOnlineSafe()) {
      set({ loading: false, offline: true });
      return { error: "Sem internet. O registo requer ligação." };
    }

    try {
      if (!isValidUsername(uname)) {
        set({ loading: false });
        return { error: "Username inválido." };
      }

      // 1️⃣ Criar Firebase Auth
      const userCred = await createUserWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );

      const fbUser = userCred.user;
      const uid = fbUser.uid;

      // 2️⃣ Verificar username (agora JÁ há auth)
      const unameRef = ref(db, `usernames/${uname}`);
      const snapU = await get(unameRef);

      if (snapU.exists()) {
        // rollback auth
        try {
          await deleteUser(fbUser);
        } catch {}

        set({ loading: false });
        return { error: "Username já existe." };
      }

      // 3️⃣ Reservar username
      await set(unameRef, uid);

      // 4️⃣ Criar registo do utilizador (ROLE = free)
      const userData = {
        uid,
        email: normalizedEmail,
        name: `${firstName} ${lastName}`.trim(),
        role: "free",
        firstName,
        lastName,
        username: uname,
      };

      await ensureUserRecord(userData);

      // ✅ agora é no-op (no-write)
      await ensureUserSubscription(uid);

      // 5️⃣ Atualizar profile Firebase Auth
      try {
        await updateProfile(fbUser, {
          displayName: `${firstName} ${lastName}`.trim(),
        });
      } catch {}

      // 6️⃣ Estado da app
      set({ user: userData, loading: false, offline: false });
      attachRolePermissionListener("free");
      attachUserListener(uid);
      attachUserPlanListener(uid);
      persistSessionSafe();
      markAuthReady();
      startEventsListenerSafe("registo");

      try {
        const inviteToken = getPendingUserInviteToken();
        if (inviteToken && auth.currentUser) {
          const idToken = await auth.currentUser.getIdToken(true);
          await fetchReferralApi("/referrals/claim-user-invite", {
            token: idToken,
            body: { token: inviteToken },
          });
          clearPendingUserInviteToken();
        }
      } catch (e) {
        console.warn("claim-user-invite após registo falhou:", e);
      }

      return true;
    } catch (err) {
      set({ loading: false, error: err?.message || "Erro no registo." });
      return { error: err?.message || "Erro no registo." };
    }
  },

  // -------------------------------------------------------------
  // RESET PASSWORD
  // -------------------------------------------------------------
  resetPassword: async (email) => {
    const normalized = (email || "").trim().toLowerCase();
    if (!normalized) return { error: "Email inválido." };

    if (!isOnlineSafe()) {
      return { error: "Sem internet. Tenta novamente quando tiveres ligação." };
    }

    try {
      const apiBase = String(
        import.meta.env.VITE_API_BASE ||
          "https://trekkin-api-snowy-sunset-6669.fly.dev"
      ).replace(/\/$/, "");
      const response = await fetch(`${apiBase}/auth/password-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalized }),
      });
      if (!response.ok) {
        throw new Error("PASSWORD_RESET_SERVER_FAILED");
      }
      return { ok: true };
    } catch (err) {
      // O envio nativo fica como redundância se o servidor estiver temporariamente
      // indisponível. Assim o utilizador não perde o acesso à recuperação.
      try {
        await sendPasswordResetEmail(auth, normalized);
        return { ok: true };
      } catch (fallbackError) {
        return {
          error:
            fallbackError?.message ||
            err?.message ||
            "Erro ao enviar email de recuperação.",
        };
      }
    }
  },

  // -------------------------------------------------------------
  // RESTORE SESSION (BOOT ONLY)
  // -------------------------------------------------------------
  restoreSession: () => {
    try {
      if (useAuthStore.getState().sessionRestored) return;

      const raw = localStorage.getItem("trekkin_session");
      if (!raw) {
        set({ sessionRestored: true, offline: !isOnlineSafe(), authReady: true });
        return;
      }

      const data = JSON.parse(raw);
      if (!data?.user?.uid) {
        set({ sessionRestored: true, offline: !isOnlineSafe(), authReady: true });
        return;
      }

      // ✅ sessão local é “fonte de verdade” offline
      set({
        user: data.user,
        permissions: data.permissions || {},
        permissionsLoaded: true,
        offline: !isOnlineSafe(),
        sessionRestored: true,
        authReady: true,
      });

      // tenta ligar permissions (se tiver net, atualiza; se não, fica cache)
      if (data.user.role) attachRolePermissionListener(data.user.role);
      if (data.user.uid) attachUserPlanListener(data.user.uid);
      markAuthReady();
      startEventsListenerSafe("sessao local");
    } catch {
      set({ sessionRestored: true, offline: !isOnlineSafe(), authReady: true });
    }
  },

  // -------------------------------------------------------------
  // ❌ ELIMINAR CONTA (APPLE / GDPR / FINAL)
  // -------------------------------------------------------------
  deleteAccount: async () => {
    const authInstance = getAuth();
    const user = authInstance.currentUser;

    if (!user) {
      throw new Error("Sem utilizador autenticado");
    }

    const uid = user.uid;

    try {
      // 1️⃣ Marcar como eliminado (auditoria opcional)
      try {
        await update(ref(db, `users/${uid}`), {
          deleted: true,
          deletedAt: Date.now(),
        });
      } catch {}

      // 2️⃣ Remover dados do Realtime DB
      await Promise.allSettled([
        remove(ref(db, `users/${uid}`)),
        remove(ref(db, `userEvents/${uid}`)),
        remove(ref(db, `userInvites/${uid}`)),
        remove(ref(db, `logs/gps/${uid}`)),
      ]);

      // 3️⃣ Apagar Firebase Auth (🔥 obrigatório Apple)
      await deleteUser(user);

      // 4️⃣ Limpar storage local
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch {}

      // 5️⃣ Reset UI
      try {
        usePanelsStore.getState().resetToMap();
      } catch {}

      // 6️⃣ Reset store
      set({
        user: null,
        users: [],
        permissions: {},
        permissionsLoaded: false,
        offline: false,
        sessionRestored: false,
        authReady: true,
      });

      return true;
    } catch (err) {
      console.error("❌ Erro ao eliminar conta:", err);

      if (String(err?.code || "").includes("requires-recent-login")) {
        throw new Error(
          "Por segurança, faz login novamente e tenta outra vez."
        );
      }

      throw err;
    }
  },

  // -------------------------------------------------------------
  // LOGOUT
  // -------------------------------------------------------------
  logout: async () => {
    try {
      const current = useAuthStore.getState().user;

      stopUserPresence();

      if (current?.uid) {
        try {
          await update(ref(db, `users/${current.uid}`), {
            online: false,
            lastSeen: serverTimestamp(),
            lastSeenText: formatDateDDMMAAAA(Date.now()),
          });
        } catch {}
      }

      if (currentPermissionUnsub) currentPermissionUnsub();
      currentPermissionUnsub = null;

      clearUserPlanListener();

      if (currentUserUnsub) {
        currentUserUnsub();
        currentUserUnsub = null;
      }

      try {
        await signOut(auth);
      } catch {}

      // ✅ parar listener de events ao sair
      try {
        const eventsStore = useEventsStore.getState();
        if (typeof eventsStore.stopListeningEvents === "function") {
          eventsStore.stopListeningEvents();
        }
      } catch {}

      // ✅ limpar estado/listeners de grupos ao sair (evita herdar currentGroup entre contas)
      try {
        const groupStore = useGroupStore.getState();
        if (typeof groupStore._stopLocationListener === "function") {
          groupStore._stopLocationListener();
        }
        if (typeof groupStore.stopListeningGroups === "function") {
          groupStore.stopListeningGroups();
        }
        useGroupStore.setState({
          groups: [],
          mygroups: [],
          myGroups: [],
          currentGroup: null,
          memberLocations: {},
          locationListenerRef: null,
          groupsListenerRef: null,
          isLoadingGroups: false,
          lastLocationSentAt: 0,
        });
      } catch {}

      localStorage.removeItem("trekkin_session");

      set({
        user: null,
        users: [],
        permissions: {},
        permissionsLoaded: false,
        offline: !isOnlineSafe(),
        sessionRestored: false,
        authReady: true,
      });
      try {
        usePanelsStore.getState().openPanel("login");
      } catch {}
    } catch {}
  },
}));

// -------------------------------------------------------------
// AUTH LISTENER
// -------------------------------------------------------------
let firebaseListenerActive = false;

export const initAuthListener = () => {
  if (firebaseListenerActive) return;
  firebaseListenerActive = true;

  // ✅ Boot: tenta restaurar sessão antes do Firebase responder
  try {
    useAuthStore.getState().restoreSession();
  } catch {}

  // ✅ online/offline flags (UI + lógica)
  try {
    const setOff = () => useAuthStore.getState().setOffline(true);
    const setOn = () => useAuthStore.getState().setOffline(false);
    window.addEventListener("offline", setOff);
    window.addEventListener("online", setOn);
  } catch {}

  // ✅ iOS/WKWebView: indexedDBLocalPersistence costuma ser mais estável
  setPersistence(auth, indexedDBLocalPersistence).catch(() => {
    setPersistence(auth, browserLocalPersistence).catch(() => {});
  });

  onAuthStateChanged(auth, async (fbUser) => {
    // 🔥 FIX CRÍTICO:
    // Se fbUser vier null (offline/restart/bug de persistence),
    // NÃO rebentamos a sessão local já restaurada.
    if (!fbUser) {
      const st = useAuthStore.getState();
      const hasLocalUser = !!st.user?.uid;

      useAuthStore.setState({
        offline: !isOnlineSafe(),
        // mantém user se existir sessão local
        user: hasLocalUser ? st.user : null,
        authReady: true,
      });

      return;
    }

    const uid = fbUser.uid;

    let data = {};
    try {
      const snap = await get(ref(db, `users/${uid}`));
      data = snap.exists() ? snap.val() : {};
    } catch {
      // offline a meio → ficamos com o mínimo do fbUser
      data = {};
    }

    if (isAppAccessBlocked(data)) {
      if (currentPermissionUnsub) {
        currentPermissionUnsub();
        currentPermissionUnsub = null;
      }
      clearUserPlanListener();
      if (currentUserUnsub) {
        currentUserUnsub();
        currentUserUnsub = null;
      }
      try {
        await signOut(auth);
      } catch {}
      localStorage.removeItem("trekkin_session");
      useAuthStore.setState({
        user: null,
        permissions: {},
        activePlan: null,
        permissionsLoaded: false,
        loading: false,
        error: BLOCKED_LOGIN_MESSAGE,
        offline: false,
        sessionRestored: false,
        authReady: true,
      });
      try {
        usePanelsStore.getState().openPanel("login");
      } catch {}
      return;
    }

    let planInfo = null;
    try {
      planInfo = await getActivePlanForUid(uid);
    } catch {}
    const planRole = planInfo?.active ? planInfo.plan : null;
    const resolvedRole = resolveProfileRole(data, planInfo);

    const base = {
      uid,
      ...data,
      email: (fbUser.email && fbUser.email.includes("@"))
        ? fbUser.email
        : (data.email && data.email.includes("@") ? data.email : null),
      name:
        data.name ||
        fbUser.displayName ||
        (fbUser.email ? fbUser.email.split("@")[0] : "Trekker"),
      role: resolvedRole || "free",
    };

    // 🔧 SANITIZE EMAIL (corrige users antigos com email=uid)
    if (base.email && data.email !== base.email) {
      try {
        await update(ref(db, `users/${uid}`), {
          email: base.email,
        });
      } catch (e) {
        console.warn("⚠️ Não foi possível corrigir email no RTDB:", e);
      }
    }

    // tentar garantir record (se offline, falha silencioso)
    try {
      await ensureUserRecord(base);
    } catch {}

    // ✅ agora é no-op (no-write)
    try {
      await ensureUserSubscription(uid);
    } catch {}

    useAuthStore.setState({
      user: base,
      activePlan: planRole || null,
      offline: false,
      authReady: true,
    });

    try {
      const xpAward = await awardDailyLoginXp(uid);
      if (xpAward?.grantedXp > 0) {
        showToast(`+${xpAward.grantedXp} XP: entrada diária concluída.`, "success");
      }
    } catch (e) {
      console.warn("⚠️ Falha ao atribuir XP diário de login:", e);
    }

    markAuthReady();
    // ✅ GARANTE que events começam APENAS depois do login
    startEventsListenerSafe("auth");
    attachRolePermissionListener(base.role);
    attachUserListener(base.uid);
    attachUserPlanListener(base.uid);
    persistSessionSafe();
  });
};
